import { useState } from 'react';
import { PageHeader, Button, Card, Badge } from 'components/ui';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { UsersIcon, PlusIcon, TrashIcon, StoreIcon } from 'components/icons';
import { useToast } from 'components/toast/ToastProvider';
import { humanizeEnum } from 'lib/formatters';
import { useVendor } from 'features/vendor';
import { shopDisplayName, useShopName } from 'components/ShopSelect';
import { useVendorStaff, useRemoveStaff } from '../hooks/useStaff';
import { AddStaffModal } from '../components/AddStaffModal';

/** Lists staff with add / remove. */
export function StaffPage() {
  const { profile } = useVendor();
  const getShopName = useShopName();
  const activeShopName = shopDisplayName(profile);
  const { staff, loading, error, refetch } = useVendorStaff();
  const { removeStaff, loading: removing } = useRemoveStaff();
  const toast = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const handleRemove = async (member) => {
    setRemovingId(member.id);
    try {
      const result = await removeStaff(member.id);
      if (result?.success) {
        toast.success('Staff removed', `${member.name || 'Staff'} no longer has access.`);
      } else {
        toast.error('Could not remove', result?.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setRemovingId(null);
    }
  };

  let content;
  if (loading && staff.length === 0) {
    content = <LoadingState label="Loading staff…" />;
  } else if (error && staff.length === 0) {
    content = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (staff.length === 0) {
    content = (
      <EmptyState
        icon={UsersIcon}
        title="No staff yet"
        description="Add staff so they can help manage your shop."
        action={
          <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setAddOpen(true)}>
            Add staff
          </Button>
        }
      />
    );
  } else {
    content = (
      <div className="space-y-3">
        {staff.map((member) => (
          <Card key={member.id} className="flex items-center gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
              {(member.name || '?').charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{member.name || 'Staff'}</p>
              <p className="text-xs text-slate-500">
                {member.phone}
                {member.staffType ? ` · ${humanizeEnum(member.staffType)}` : ''}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                <StoreIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {getShopName(member.ownerVendorId) || activeShopName}
                </span>
              </p>
            </div>
            {Array.isArray(member.privileges) && member.privileges.length > 0 && (
              <Badge tone="neutral">
                {member.privileges.length} privilege{member.privileges.length === 1 ? '' : 's'}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${member.name || 'staff'}`}
              isLoading={removing && removingId === member.id}
              onClick={() => handleRemove(member)}
              leftIcon={<TrashIcon className="h-4 w-4" />}
            />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Staff"
        description="People who can help manage your shop."
        actions={
          staff.length > 0 && (
            <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setAddOpen(true)}>
              Add staff
            </Button>
          )
        }
      />
      {content}
      <AddStaffModal isOpen={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

export default StaffPage;
